import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type QA = { q: string; a: string };
type Category = { title: string; items: QA[] };

const categories: Category[] = [
  {
    title: "Über FINABLE & unsere Beratung",
    items: [
      {
        q: "Ist FINABLE eine Bank oder ein Versicherungsunternehmen?",
        a: "Nein. FINABLE ist eine unabhängige Finanzberatung ohne Bankbindung und ohne eigene Produkte. Wir empfehlen ausschließlich Produkte die zu Dir passen — nicht solche an denen wir verdienen.",
      },
      {
        q: "Ist die Erstberatung wirklich kostenlos?",
        a: "Ja, vollständig. Das erste Gespräch ist kostenlos und unverbindlich. Es entstehen keine versteckten Kosten – Du gehst keinerlei Verpflichtung ein.",
      },
      {
        q: "Was macht FINABLE und für wen ist die Beratung geeignet?",
        a: "FINABLE ist ein unabhängiger digitaler Finanzberater mit Sitz in Hamburg. Wir beraten Privatpersonen bundesweit zu den Themen steueroptimierte Altersvorsorge, staatliche Förderungen, Vermögensaufbau und Absicherung. Unsere Beratung richtet sich an Angestellte, Selbstständige und Unternehmer die ihr Geld smart anlegen, Steuern sparen und langfristig Vermögen aufbauen wollen – ohne Bankprodukte und ohne versteckte Kosten.",
      },
      {
        q: "Was kostet das Erstgespräch bei FINABLE?",
        a: "Das Erstgespräch ist vollständig kostenlos und unverbindlich. Wir lernen uns kennen, analysieren Deine aktuelle Situation und zeigen Dir konkrete Möglichkeiten auf. Erst wenn Du Dich für eine Zusammenarbeit entscheidest, besprechen wir transparent wie eine mögliche Vergütung aussieht – bevor Du irgendetwas unterschreibst.",
      },
      {
        q: "Was bedeutet \"unabhängige Beratung\" bei FINABLE?",
        a: "Wir sind nicht an eine Bank, Versicherung oder einen bestimmten Produktanbieter gebunden. Das bedeutet: Wir suchen für Dich aus dem gesamten Markt die beste Lösung – nicht die Lösung die uns die höchste Provision bringt. Du bekommst eine ehrliche Empfehlung die wirklich zu Deiner Situation passt.",
      },
      {
        q: "Wie läuft eine Beratung bei FINABLE ab?",
        a: "In vier Schritten: Erstgespräch (kostenlos, ca. 45 Minuten) – wir lernen Dich kennen und verstehen Deine Ziele. Analyse & Konzept – wir entwickeln eine individuelle Strategie für Dich. Umsetzung – wir setzen die Strategie gemeinsam mit Dir um. Langfristige Betreuung – wir begleiten Dich dauerhaft und passen die Strategie bei Bedarf an.",
      },
      {
        q: "Berät FINABLE auch außerhalb von Hamburg?",
        a: "Ja – wir beraten komplett digital und bundesweit. Ob Hamburg, München, Berlin oder ein kleines Dorf – Du brauchst nur einen Internetzugang. Alle Gespräche finden per Video oder Telefon statt, Dokumente werden digital übermittelt und unterschrieben.",
      },
    ],
  },
  {
    title: "Steuern sparen & Steueroptimierung",
    items: [
      {
        q: "Wie kann ich als Angestellter legal Steuern sparen?",
        a: "Als Angestellter hast Du mehr Möglichkeiten als die meisten denken. Die wichtigsten Hebel: Altersvorsorge über Rürup oder bAV steuerlich absetzen, Homeoffice-Pauschale (6 € pro Tag, max. 1.260 € jährlich) nutzen, Pendlerpauschale geltend machen, Fortbildungskosten absetzen und den Sparer-Pauschbetrag von 1.000 € vollständig ausschöpfen. In Summe können das mehrere hundert bis tausend Euro Ersparnis pro Jahr sein.",
      },
      {
        q: "Was ist die Rürup-Rente und wie viel Steuern spare ich damit?",
        a: "Die Rürup-Rente (Basisrente) ist eine staatlich geförderte Altersvorsorge bei der Du Beiträge bis zu 30.826 € jährlich (2026, Ledige) vollständig von der Steuer absetzen kannst. Je nach persönlichem Steuersatz entspricht das einer Ersparnis von bis zu mehreren Tausend Euro pro Jahr. Besonders attraktiv ist sie für Selbstständige, Freiberufler und Gutverdiener ab ca. 40.000 € Jahreseinkommen.",
      },
      {
        q: "Was ist die betriebliche Altersvorsorge (bAV) und lohnt sie sich?",
        a: "Bei der bAV wandelst Du einen Teil Deines Bruttogehalts direkt in Rentenansprüche um – bevor Steuern und Sozialabgaben abgezogen werden. 2026 sind bis 8.112 € jährlich (676 €/Monat, 8 % der BBG) lohnsteuerfrei; davon – nicht zusätzlich dazu – sind die ersten 4.056 € (338 €/Monat, 4 %) auch sozialabgabenfrei. Dein Arbeitgeber ist gesetzlich verpflichtet, mindestens 15 % als Zuschuss oben draufzulegen. Wer 200 € monatlich einzahlt, zahlt netto oft nur 100–120 € weniger – der Rest kommt vom Staat und Arbeitgeber.",
      },
      {
        q: "Was ist der Unterschied zwischen Steueroptimierung und Steuerhinterziehung?",
        a: "Steueroptimierung bedeutet alle gesetzlich erlaubten Möglichkeiten zu nutzen um die Steuerlast zu reduzieren – das ist vollkommen legal und sogar vom Gesetzgeber so vorgesehen. Steuerhinterziehung ist das bewusste Verschweigen von Einkünften oder falsche Angaben gegenüber dem Finanzamt – das ist illegal. Bei FINABLE arbeiten wir ausschließlich mit legalen Steueroptimierungsstrategien.",
      },
      {
        q: "Wie viel Steuern kann ich als Angestellter sparen?",
        a: "Das hängt von Deiner persönlichen Situation ab. Im Durchschnitt sparen unsere Kunden zwischen 500 und 2.000 Euro pro Jahr durch optimale Nutzung von Freibeträgen, Werbungskosten und steueroptimierten Anlageprodukten.",
      },
      {
        q: "Was sind die wichtigsten Steuertipps für Arbeitnehmer 2026?",
        a: "Die wichtigsten Hebel sind: Homeoffice-Pauschale (6 € pro Tag, max. 1.260 €), erhöhter Arbeitnehmer-Pauschbetrag (1.230 €), Pendlerpauschale (0,38 € ab dem 21. km), Rürup-Beiträge (bis 30.826 € absetzbar, Ledige) und betriebliche Altersvorsorge.",
      },
    ],
  },
  {
    title: "Staatliche Förderungen & Altersvorsorge",
    items: [
      {
        q: "Welche staatlichen Förderungen gibt es für die Altersvorsorge?",
        a: "Es gibt mehrere attraktive Förderungen: Die Riester-Zulage (175 € Grundzulage + 300 € pro Kind jährlich), den steuerlichen Abzug von Rürup-Beiträgen (bis 30.826 € jährlich, Ledige 2026), den Arbeitgeberzuschuss zur bAV (mindestens 15 % Pflicht), und lohnsteuerfreie Entgeltumwandlung bis 8.112 € jährlich (676 €/Monat, 2026) – davon 4.056 € auch sozialabgabenfrei. Viele Menschen lassen diese Förderungen ungenutzt verfallen – obwohl der Staat ihnen buchstäblich Geld schenkt.",
      },
      {
        q: "Lohnt sich die Riester-Rente noch?",
        a: "Für Familien mit Kindern ist Riester nach wie vor sehr attraktiv – wegen der hohen Kinderzulage von 300 € pro Kind jährlich. Für Gutverdiener ohne Kinder ist oft der Steuervorteil interessanter als die Zulage. Entscheidend ist das richtige Produkt: Riester-Fondssparpläne bieten deutlich bessere Konditionen als klassische Rentenversicherungen. Wir prüfen kostenlos ob und welches Riester-Produkt für Dich sinnvoll ist.",
      },
      {
        q: "Was ist die Rentenlücke und wie schließe ich sie?",
        a: "Die Rentenlücke ist die Differenz zwischen Deiner voraussichtlichen gesetzlichen Rente und dem Betrag den Du im Alter tatsächlich brauchst. Da das Rentenniveau in Deutschland seit Jahren sinkt, reicht die gesetzliche Rente für die meisten Menschen nicht mehr aus. Wir berechnen Deine persönliche Rentenlücke kostenlos und zeigen Dir wie Du sie mit staatlich geförderten Instrumenten schließt.",
      },
      {
        q: "Ab wann sollte ich mit der Altersvorsorge anfangen?",
        a: "So früh wie möglich – am besten heute. Der Zinseszinseffekt ist der mächtigste Mechanismus beim Vermögensaufbau: Wer mit 25 anfängt 200 € monatlich zu investieren, hat mit 65 bei 7 % Rendite über 520.000 €. Wer erst mit 35 anfängt kommt auf weniger als die Hälfte – obwohl der Unterschied nur 10 Jahre ist. Jeder Monat den Du wartest kostet Dich langfristig Geld.",
      },
      {
        q: "Wie groß ist die Rentenlücke in Deutschland durchschnittlich?",
        a: "Die durchschnittliche Rentenlücke in Deutschland beträgt laut Studien zwischen 700 und 1.200 € pro Monat. Das bedeutet: Die gesetzliche Rente deckt im Schnitt nur 48 % des letzten Nettoeinkommens ab. Wer den gewohnten Lebensstandard halten will, muss die Differenz aus privater und betrieblicher Vorsorge schließen.",
      },
      {
        q: "Welche staatlichen Förderungen stehen mir als Arbeitnehmer zu?",
        a: "Die wichtigsten sind: Arbeitnehmer-Sparzulage (9 % auf vermögenswirksame Leistungen, max. 43 €/Jahr), Wohnungsbauprämie (10 % auf Bauspareinlagen), Riester-Zulagen (bis 175 € Grundzulage + 300 € pro Kind) und bAV-Entgeltumwandlung bis 676 €/Monat lohnsteuerfrei (2026) – davon 338 €/Monat auch sozialabgabenfrei.",
      },
      {
        q: "Was sind vermögenswirksame Leistungen (VL)?",
        a: "VL sind Zahlungen Deines Arbeitgebers in einen Sparvertrag — bis zu 40 € pro Monat. Viele Arbeitnehmer wissen nicht, dass sie Anspruch darauf haben oder nutzen sie nicht optimal. Bei niedrigem bis mittlerem Einkommen gibt es dazu noch die staatliche Arbeitnehmer-Sparzulage.",
      },
    ],
  },
  {
    title: "Geldanlage & Investments",
    items: [
      {
        q: "Wie soll ich mein Geld am besten anlegen?",
        a: "Das hängt von Deiner Situation, Deinen Zielen und Deinem Anlagehorizont ab. Als Grundprinzip gilt: Notgroschen (3–6 Monatsgehälter) auf dem Tagesgeldkonto, langfristiges Kapital in breit gestreute ETFs investieren, und Altersvorsorgekapital steueroptimiert über Versicherungsmantel oder geförderte Produkte anlegen. Eine individuelle Strategie entwickeln wir gemeinsam im kostenlosen Erstgespräch.",
      },
      {
        q: "Was sind ETFs und warum empfiehlt FINABLE sie?",
        a: "ETFs (Exchange Traded Funds) sind börsengehandelte Indexfonds die einen ganzen Markt abbilden – z.B. die 1.500 größten Unternehmen weltweit (MSCI World). Sie sind kostengünstig (Kosten ab 0,07 % jährlich), breit gestreut und haben historisch langfristig 7–9 % Rendite pro Jahr erzielt. Im Vergleich zu aktiv verwalteten Fonds schlagen sie die meisten Fondsmanager über lange Zeiträume – bei deutlich niedrigeren Kosten.",
      },
      {
        q: "Sind breit gestreute ETFs weniger rentabel?",
        a: "Nein – das ist ein verbreiteter Mythos. Breit gestreute ETFs haben in den letzten 10 Jahren ähnliche oder sogar bessere Renditen erzielt als aktiv verwaltete Fonds. Die passiven Indexfonds schlagen die meisten Fondsmanager über lange Zeiträume – bei deutlich niedrigeren Kosten. Bei FINABLE legen wir Wert auf maximale Rendite bei minimalem Risiko: Dein Geld soll effizient für Dich arbeiten.",
      },
      {
        q: "Wie viel Geld brauche ich um bei FINABLE anzufangen?",
        a: "Es gibt keine Mindestanlagesumme. Mit einem ETF-Sparplan kannst Du bereits ab 25 € monatlich starten. Entscheidend ist nicht die Höhe des ersten Betrags sondern die Regelmäßigkeit. Wir entwickeln eine Strategie die zu Deinem aktuellen Einkommen und Deinen Zielen passt – egal ob Du 50 € oder 5.000 € monatlich anlegen kannst.",
      },
    ],
  },
  {
    title: "Versicherungen & Absicherung",
    items: [
      {
        q: "Welche Versicherungen brauche ich wirklich?",
        a: "Die absolute Mindestabsicherung für jeden: Private Haftpflichtversicherung (unverzichtbar, ab ca. 5 € monatlich) und Berufsunfähigkeitsversicherung (schützt Dein Einkommen wenn Du nicht mehr arbeiten kannst). Für Familien kommt eine Risikolebensversicherung hinzu. Alles andere hängt von Deiner persönlichen Situation ab. Wir analysieren kostenlos welche Versicherungen Du wirklich brauchst – und welche Du nicht brauchst.",
      },
      {
        q: "Was ist eine Berufsunfähigkeitsversicherung und warum ist sie so wichtig?",
        a: "Jeder vierte Deutsche wird im Laufe seines Berufslebens berufsunfähig – durch Krankheit, Unfall oder psychische Erkrankung. Die gesetzliche Erwerbsminderungsrente reicht dafür bei weitem nicht aus. Eine Berufsunfähigkeitsversicherung zahlt Dir eine monatliche Rente wenn Du Deinen Beruf nicht mehr ausüben kannst – und schützt damit Dein wichtigstes Kapital: Deine Arbeitskraft.",
      },
      {
        q: "Wie findet FINABLE die beste Versicherung für mich?",
        a: "Als unabhängige Makler haben wir Zugang zum gesamten Versicherungsmarkt – über 200 Anbieter. Wir vergleichen Konditionen, Leistungen und Preis-Leistungs-Verhältnis ohne an einen bestimmten Anbieter gebunden zu sein. Du bekommst die Lösung die wirklich zu Dir passt – nicht die die uns die höchste Provision bringt.",
      },
    ],
  },
  {
    title: "Krankenversicherung",
    items: [
      {
        q: "Was ist der Unterschied zwischen GKV und PKV?",
        a: "In der GKV zahlt man einen einkommensabhängigen Beitrag und erhält Standardleistungen. In der PKV zahlt man einen risikoabhängigen Beitrag (Alter, Gesundheit) und kann Leistungen individuell wählen. Die PKV bietet oft bessere Leistungen, kann aber im Alter teurer werden.",
      },
      {
        q: "Wann lohnt sich der Wechsel in die private Krankenversicherung (PKV)?",
        a: "Ein Wechsel lohnt sich vor allem für gut verdienende Singles oder kinderlose Paare, die über der Jahresarbeitsentgeltgrenze (2026: 77.400 € brutto/Jahr, PKV-Bestand 69.750 €) verdienen. Für Familien mit Kindern ist die GKV oft günstiger, da Kinder dort beitragsfrei mitversichert sind.",
      },
    ],
  },
];

export default function FAQ() {
  const [activeCategory, setActiveCategory] = useState(0);
  const allFaqs = categories.flatMap((c) => c.items);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: allFaqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };

  // JSON-LD emitted via Helmet in the returned JSX (SSG-visible).


  return (
    <section className="py-20 md:py-28 bg-background">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <div className="container max-w-3xl">
        <div className="text-center reveal">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-primary text-balance">
            Häufig gestellte Fragen zur Finanzberatung
          </h2>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Antworten zu Altersvorsorge, staatlichen Förderungen, Steueroptimierung, ETF-Investments und Versicherungen – verständlich erklärt.
          </p>
        </div>

        <div className="mt-12 reveal">
          <Select
            value={String(activeCategory)}
            onValueChange={(v) => setActiveCategory(Number(v))}
          >
            <SelectTrigger aria-label="FAQ-Kategorie wählen" className="w-full max-w-md mx-auto h-12 rounded-xl bg-card border-border text-primary font-semibold shadow-card">
              <SelectValue placeholder="Kategorie wählen" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat, ci) => (
                <SelectItem key={ci} value={String(ci)} className="text-base">
                  {cat.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="mt-8">
            <h3 className="text-accent font-semibold uppercase tracking-wider text-sm mb-4 text-center">
              {categories[activeCategory].title}
            </h3>
            <Accordion type="single" collapsible className="space-y-3">
              {categories[activeCategory].items.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${activeCategory}-${i}`}
                  className="rounded-xl border border-border bg-card px-5 shadow-card data-[state=open]:shadow-elegant transition-all"
                >
                  <AccordionTrigger className="text-left font-display text-lg font-semibold text-primary hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}
